import React, { useState, useEffect, useMemo } from "react";

// @mui material components
import Divider from "@mui/material/Divider";

// Soft UI Dashboard React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";
import Grid from "@mui/material/Grid";

// Custom styles for the Modal
import ModalRoot from "containers/modal/ModalRoot";
import { Checkbox, FormControlLabel, Icon } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { AddProduct, EditProduct } from "store/features/dashboard";
import CustomButton from "containers/CustomButton";
import ModalHeader from "containers/modal-header";
import { updateFirstLetter } from "utils/helper";
import { userSelector } from "store/features/authSlice";
import Createable from "containers/Select";

function Modal({
  open,
  setOpenModal,
  handleCloseModal,
  uid,
  selectedItem,
  setSelectedItem,
  updateProductCountHandler,
  isSuccess,
  isFetching,
  isError,
  message,
  mode,
  editHandler,
  viewHandler,
}) {
  const [hsnCodeOptions, setHsnCodeOptions] = useState([]);
  const { hsnCode: hsnCodeData, unitList } = useSelector(userSelector);

  useEffect(() => {
    const allHSnSac = [...hsnCodeData?.data.hsn, ...hsnCodeData?.data.sac];
    const updated = allHSnSac.map((item) => {
      return { label: `${item.hsn} - ${item.description}`, value: item.hsn };
    });
    setHsnCodeOptions(updated);
  }, [hsnCodeData]);

  //default modal fields value
  const defaultHandler = useMemo(() => {
    return {
      productName: null,
      brand: null,
      description: null,
      costPriceWOT: null,
      gst: 0,
      mrp: null,
      availableQuantity: null,
      checkGst: null,
      gstType: { label: "CGST & SGST", value: "CGST & SGST" },
      igst: null,
      sgst: 0,
      cgst: 0,
      gstComment: null,
      gstNumber: null,
      unit: null,
    };
  }, []);

  //default modal fields value
  const errorDefault = useMemo(() => {
    return {
      productName: false,
      brand: false,
      description: false,
      costPriceWOT: false,
      gst: false,
      mrp: false,
      availableQuantity: false,
    };
  }, []);

  const dispatch = useDispatch();

  const [formdata, setFormdata] = useState(defaultHandler);

  const [errorMessage, setErrorMessage] = useState(errorDefault);

  //for handling selected items
  useEffect(() => {
    setFormdata({
      ...defaultHandler,
      ...selectedItem,
    });
  }, [selectedItem, defaultHandler]);

  useEffect(() => {
    if (formdata?.checkGst) {
      setFormdata({
        ...formdata,
        gst:
          formdata.gstType.value === "IGST"
            ? formdata?.igst ?? 0
            : formdata?.sgst + formdata?.cgst ?? 0,
      });
    }
  }, [
    formdata?.gstType?.value,
    formdata.checkGst,
    formdata?.igst,
    formdata?.sgst,
    formdata?.cgst,
  ]);

  //for submitting data
  const submitHandler = async (e) => {
    const gstNumberRegex =
      /\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}/;
    const gstNumberMatch = formdata?.gstNumber?.match(gstNumberRegex);

    if (!formdata.productName) {
      toast.error("Please enter a Product Name");
      setErrorMessage({
        ...errorMessage,
        productName: true,
      });
      return;
    }

    if (!formdata.brand) {
      toast.error("Please enter a Product Brand ");
      setErrorMessage({
        ...errorMessage,
        brand: true,
      });
      return;
    }

    if (!formdata.description) {
      toast.error("Please fill Product Description");
      setErrorMessage({
        ...errorMessage,
        description: true,
      });
      return;
    }

    if (!formdata.costPriceWOT) {
      toast.error("Please fill Cost Price");
      setErrorMessage({
        ...errorMessage,
        costPriceWOT: true,
      });
      return;
    }

    if (!formdata.mrp) {
      toast.error("Please fill MRP");
      setErrorMessage({
        ...errorMessage,
        mrp: true,
      });
      return;
    }

    if (!formdata.availableQuantity) {
      toast.error("Please fill Available Quantity field");
      setErrorMessage({
        ...errorMessage,
        availableQuantity: true,
      });
      return;
    }

    if (!formdata.unit) {
      toast.error("Please fill Unit field");
      setErrorMessage({
        ...errorMessage,
        unit: true,
      });
      return;
    }

    if (formdata?.checkGst) {
      if (
        !formdata?.gstNumber?.length ||
        formdata?.gstNumber === undefined ||
        formdata?.gstNumber === null
      ) {
        return toast.error("Please fill GST number!!");
      } else if (!gstNumberMatch) {
        return toast.error("Invalid GST number!!");
      }

      if (formdata?.gstType.value === "IGST") {
        if (!formdata.igst) {
          toast.error("Please fill IGST field");
          setErrorMessage({
            ...errorMessage,
            igst: true,
          });
          return;
        }
      } else {
        if (!formdata.sgst) {
          toast.error("Please fill SGST field");
          setErrorMessage({
            ...errorMessage,
            sgst: true,
          });
          return;
        }

        if (!formdata.cgst) {
          toast.error("Please fill CGST field");
          setErrorMessage({
            ...errorMessage,
            cgst: true,
          });
          return;
        }
      }
    }

    if (selectedItem) {
      dispatch(EditProduct({ formdata, uid }));
      return;
    }
    dispatch(AddProduct({ formdata, uid }));
  };

  useEffect(() => {
    if (isSuccess && open) {
      handleCloseModal();

      setTimeout(() => {
        setSelectedItem(null);

        setFormdata({
          ...defaultHandler,
        });
        setErrorMessage({
          ...errorDefault,
        });
      }, 100);
    }
  }, [
    isSuccess,
    handleCloseModal,
    dispatch,
    isError,
    errorDefault,
    setSelectedItem,
    updateProductCountHandler,
    defaultHandler,
    message,
    open,
  ]);

  return (
    <ModalRoot
      variant="permanent"
      ownerState={{ openConfigurator: open }}
      width="50%"
    >
      <SuiBox
        style={{ whiteSpace: "normal !important" }}
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        pt={3}
        pb={0.8}
        px={3}
      >
        <ModalHeader
          mode={mode}
          title="Product"
          subtitle={`Product details of ${updateFirstLetter(
            formdata?.productName
          )}`}
        />

        <Grid item xs={12}>
          {mode === "view" && (
            <Icon
              sx={({
                typography: { size, fontWeightBold },
                palette: { dark },
              }) => ({
                fontSize: `${size.md} !important`,
                fontWeight: `${fontWeightBold} !important`,
                stroke: dark.main,
                strokeWidth: "2px",
                cursor: "pointer",
                mt: 2,
                mr: 2,
              })}
              onClick={() => editHandler(selectedItem)}
            >
              edit
            </Icon>
          )}
          {mode === "edit" && (
            <Icon
              sx={({
                typography: { size, fontWeightBold },
                palette: { dark },
              }) => ({
                fontSize: `${size.md} !important`,
                fontWeight: `${fontWeightBold} !important`,
                stroke: dark.main,
                strokeWidth: "2px",
                cursor: "pointer",
                mt: 2,
                mr: 2,
              })}
              onClick={() => viewHandler(selectedItem)}
            >
              visibility
            </Icon>
          )}
          <Icon
            sx={({
              typography: { size, fontWeightBold },
              palette: { dark },
            }) => ({
              fontSize: `${size.md} !important`,
              fontWeight: `${fontWeightBold} !important`,
              stroke: dark.main,
              strokeWidth: "2px",
              cursor: "pointer",
              mt: 2,
            })}
            onClick={() => {
              handleCloseModal();

              setTimeout(() => {
                if (selectedItem) {
                  setSelectedItem(null);
                }
                setFormdata({
                  ...defaultHandler,
                });
                setErrorMessage({
                  ...errorDefault,
                });
              }, 500);
            }}
          >
            close
          </Icon>
        </Grid>
      </SuiBox>

      <Divider />

      <SuiBox pt={1.25} pb={3} px={3}>
        <SuiBox mt={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <Grid container spacing={3}>
                <Grid item xs={12} lg={6}>
                  <SuiBox lineHeight={1}>
                    <SuiTypography mb={1} variant="h6">
                      Product Name*
                    </SuiTypography>
                    <SuiInput
                      disabled={mode === "view"}
                      type="text"
                      name="productName"
                      readOnly={mode === "view"}
                      error={errorMessage?.productName}
                      value={formdata.productName ?? ""}
                      onChange={(e) => {
                        setErrorMessage({
                          ...errorMessage,
                          [e.target.name]: false,
                        });
                        setFormdata({
                          ...formdata,
                          [e.target.name]: e.target.value,
                        });
                      }}
                    />
                  </SuiBox>
                </Grid>

                <Grid item xs={12} lg={6}>
                  <SuiBox lineHeight={1}>
                    <SuiTypography mb={1} variant="h6">
                      Product Brand*
                    </SuiTypography>
                    <SuiInput
                      disabled={mode === "view"}
                      type="text"
                      name="brand"
                      readOnly={mode === "view"}
                      error={errorMessage?.brand}
                      value={formdata.brand ?? ""}
                      onChange={(e) => {
                        setErrorMessage({
                          ...errorMessage,
                          [e.target.name]: false,
                        });
                        setFormdata({
                          ...formdata,
                          [e.target.name]: e.target.value,
                        });
                      }}
                    />
                  </SuiBox>
                </Grid>
              </Grid>

              <Grid container spacing={3}>
                <Grid item xs={12} lg={6}>
                  <SuiBox mt={3}>
                    <SuiTypography mb={1} variant="h6">
                      Cost Price*
                    </SuiTypography>

                    <SuiInput
                      disabled={mode === "view"}
                      type="number"
                      error={errorMessage?.costPriceWOT}
                      placeholder=""
                      readOnly={mode === "view"}
                      name="costPriceWOT"
                      value={formdata.costPriceWOT ?? ""}
                      onChange={(e) => {
                        let numberRegex = /^[0-9][0-9]{0,8}?(\.[0-9]{1,2})?$/;
                        let val = e.target.value;
                        let match_ = val.match(numberRegex);
                        if (!match_ && val) {
                          return false;
                        }
                        setErrorMessage({
                          ...errorMessage,
                          [e.target.name]: false,
                        });
                        setFormdata({
                          ...formdata,
                          [e.target.name]: parseFloat(e.target.value),
                        });
                      }}
                    />
                  </SuiBox>
                </Grid>
                <Grid item xs={12} lg={6}>
                  <SuiBox mt={3}>
                    <SuiTypography mb={1} variant="h6">
                      MRP*
                    </SuiTypography>

                    <SuiInput
                      disabled={mode === "view"}
                      type="number"
                      placeholder=""
                      readOnly={mode === "view"}
                      name="mrp"
                      error={errorMessage?.mrp}
                      value={formdata?.mrp ?? ""}
                      onChange={(e) => {
                        let numberRegex = /^[0-9][0-9]{0,8}?(\.[0-9]{1,2})?$/;
                        let val = e.target.value;
                        let match_ = val.match(numberRegex);
                        if (!match_ && val) {
                          return false;
                        }
                        setErrorMessage({
                          ...errorMessage,
                          mrp: false,
                        });
                        setFormdata({
                          ...formdata,
                          [e.target.name]: parseFloat(e.target.value),
                        });
                      }}
                    />
                  </SuiBox>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} lg={4}>
              <SuiBox lineHeight={1}>
                <SuiTypography mb={1} variant="h6">
                  Product Description*
                </SuiTypography>
                <SuiInput
                  disabled={mode === "view"}
                  type="text"
                  name="description"
                  placeholder="product description ..."
                  readOnly={mode === "view"}
                  rows={6}
                  multiline
                  error={errorMessage?.description}
                  value={formdata.description ?? ""}
                  onChange={(e) => {
                    setErrorMessage({
                      ...errorMessage,
                      [e.target.name]: false,
                    });
                    setFormdata({
                      ...formdata,
                      [e.target.name]: e.target.value,
                    });
                  }}
                />
              </SuiBox>
            </Grid>
          </Grid>
        </SuiBox>

        <SuiBox mt={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={4}>
              <SuiBox>
                <SuiTypography mb={1} variant="h6">
                  Available Quantity*
                </SuiTypography>

                <SuiInput
                  disabled={mode === "view"}
                  type="number"
                  placeholder=""
                  readOnly={mode === "view"}
                  name="availableQuantity"
                  error={errorMessage?.availableQuantity}
                  value={formdata?.availableQuantity ?? ""}
                  onChange={(e) => {
                    setErrorMessage({
                      ...errorMessage,
                      [e.target.name]: false,
                    });
                    setFormdata({
                      ...formdata,
                      [e.target.name]: parseInt(e.target.value.replace(/\D/g)),
                    });
                  }}
                />
              </SuiBox>
            </Grid>
            <Grid item xs={12} lg={4}>
              <SuiBox>
                <SuiTypography mb={1} variant="h6">
                  HSN/SAC
                </SuiTypography>
                <Createable
                  disabled={mode === "view"}
                  options={hsnCodeOptions ?? []}
                  error={errorMessage?.hsnCode}
                  value={formdata?.hsnCode}
                  style={{ borderRadius: "0.5rem", fontSize: 14 }}
                  inputStyle={{
                    width: 190,
                    height: "1.800rem",
                    borderRadius: "0.5rem",
                  }}
                  placeholder="Choose HSN/SAC"
                  onChange={(e) => {
                    setErrorMessage({
                      ...errorMessage,
                      hsnCode: false,
                    });

                    setFormdata({
                      ...formdata,
                      hsnCode: e,
                    });
                  }}
                />
              </SuiBox>
            </Grid>

            <Grid item xs={12} lg={4}>
              <SuiBox>
                <SuiTypography mb={1} variant="h6">
                  Unit*
                </SuiTypography>
                <Createable
                  disabled={mode === "view"}
                  options={unitList ?? []}
                  error={errorMessage?.unit}
                  value={formdata?.unit}
                  style={{ borderRadius: "0.5rem", fontSize: 14 }}
                  inputStyle={{
                    width: 190,
                    height: "1.800rem",
                    borderRadius: "0.5rem",
                  }}
                  placeholder="Choose unit"
                  onChange={(e) => {
                    setErrorMessage({
                      ...errorMessage,
                      unit: false,
                    });

                    setFormdata({
                      ...formdata,
                      unit: e,
                    });
                  }}
                />
              </SuiBox>
            </Grid>
          </Grid>
        </SuiBox>

        {/* <SuiBox mt={3}>
          <FormControlLabel
            control={
              <Checkbox
                disabled={mode === "view"}
                checked={formdata?.checkGst}
                onChange={() => {
                  setFormdata({
                    ...formdata,
                    checkGst: !formdata?.checkGst,
                  });
                }}
              />
            }
            label="GST"
            sx={{ marginLeft: 0, width: "fit-content" }}
          />
        </SuiBox>

        {formdata?.checkGst && (
          <SuiBox mt={3}>
            <GST
              formdata={formdata}
              setFormdata={setFormdata}
              setErrorMessage={setErrorMessage}
              errorMessage={errorMessage}
              mode={mode}
            />
          </SuiBox>
        )} */}

        <Divider />

        {mode !== "view" && (
          <SuiBox mt={3} mb={2} display="flex" justifyContent="center">
            <SuiBox mb={2} width="32%">
              <CustomButton
                color="dark"
                variant="gradient"
                onClick={submitHandler}
                disabled={isFetching || !open}
                fullWidth
                width={180}
                title={selectedItem ? "Edit Product" : "Add Product"}
                loader={isFetching}
              />
            </SuiBox>
          </SuiBox>
        )}
      </SuiBox>
    </ModalRoot>
  );
}

export default Modal;
